import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyInventorySettings.css";

const API_URL = "/api";

const emptyForm = {
  default_minimum_stock: 0,
  enable_low_stock_alerts: true,
  alert_when_out_of_stock: true,
  allow_negative_stock: false,
  require_note_for_adjustments: true,
  require_note_for_exits: true,
  allow_manual_movements: true,
  is_active: true,
};

const normalizeInventorySettings = (data = {}) => ({
  ...emptyForm,
  ...data,
  default_minimum_stock:
    data.default_minimum_stock ?? 0,
  enable_low_stock_alerts:
    data.enable_low_stock_alerts ?? true,
  alert_when_out_of_stock:
    data.alert_when_out_of_stock ?? true,
  allow_negative_stock:
    data.allow_negative_stock ?? false,
  require_note_for_adjustments:
    data.require_note_for_adjustments ?? true,
  require_note_for_exits:
    data.require_note_for_exits ?? true,
  allow_manual_movements:
    data.allow_manual_movements ?? true,
  is_active: data.is_active ?? true,
});

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

export default function CompanyInventorySettings({
  readOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/settings/inventory/me`,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeInventorySettings(response.data));
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar la configuración de inventario."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (readOnly) {
      return;
    }

    const defaultMinimumStock = Number(
      form.default_minimum_stock || 0
    );

    if (defaultMinimumStock < 0) {
      toast.warning(
        "El stock mínimo predeterminado no puede ser negativo."
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        default_minimum_stock: defaultMinimumStock,
        enable_low_stock_alerts:
          form.enable_low_stock_alerts,
        alert_when_out_of_stock:
          form.alert_when_out_of_stock,
        allow_negative_stock:
          form.allow_negative_stock,
        require_note_for_adjustments:
          form.require_note_for_adjustments,
        require_note_for_exits:
          form.require_note_for_exits,
        allow_manual_movements:
          form.allow_manual_movements,
        is_active: form.is_active,
      };

      const response = await axios.put(
        `${API_URL}/settings/inventory/me`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeInventorySettings(response.data));

      toast.success("Configuración de inventario actualizada.");
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible guardar la configuración de inventario."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        Cargando configuración de inventario...
      </div>
    );
  }

  return (
    <form
      className="inventory-settings-form"
      onSubmit={handleSubmit}
    >
      <div className="inventory-settings-heading">
        <div>
          <h3>Configuración de inventario</h3>
          <p>
            Define alertas y controles para proteger las
            existencias de tus productos.
          </p>
        </div>

        <span
          className={
            form.is_active
              ? "inventory-status active"
              : "inventory-status inactive"
          }
        >
          {form.is_active
            ? "Inventario activo"
            : "Inventario inactivo"}
        </span>
      </div>

      <fieldset
        className="inventory-settings-fieldset"
        disabled={readOnly}
      >
        <section className="inventory-settings-section">
          <h4>Stock y alertas</h4>

          <div className="inventory-settings-grid">
            <div className="floating-field">
              <input
                id="default_minimum_stock"
                name="default_minimum_stock"
                type="number"
                min="0"
                value={form.default_minimum_stock}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="default_minimum_stock">
                Stock mínimo predeterminado
              </label>
            </div>
          </div>

          <div className="inventory-switches">
            <label className="inventory-switch">
              <input
                type="checkbox"
                name="enable_low_stock_alerts"
                checked={form.enable_low_stock_alerts}
                onChange={handleChange}
              />
              <span>
                Generar alertas cuando un producto alcance
                su stock mínimo
              </span>
            </label>

            <label className="inventory-switch">
              <input
                type="checkbox"
                name="alert_when_out_of_stock"
                checked={form.alert_when_out_of_stock}
                onChange={handleChange}
              />
              <span>
                Generar alertas cuando un producto se quede
                sin existencias
              </span>
            </label>

            <label className="inventory-switch inventory-warning">
              <input
                type="checkbox"
                name="allow_negative_stock"
                checked={form.allow_negative_stock}
                onChange={handleChange}
              />
              <span>
                Permitir movimientos que dejen el stock
                en negativo
              </span>
            </label>
          </div>
        </section>

        <section className="inventory-settings-section">
          <h4>Control de movimientos</h4>

          <div className="inventory-switches">
            <label className="inventory-switch">
              <input
                type="checkbox"
                name="allow_manual_movements"
                checked={form.allow_manual_movements}
                onChange={handleChange}
              />
              <span>
                Permitir registrar entradas y salidas manuales
              </span>
            </label>

            <label className="inventory-switch">
              <input
                type="checkbox"
                name="require_note_for_adjustments"
                checked={form.require_note_for_adjustments}
                onChange={handleChange}
              />
              <span>
                Exigir una nota para realizar ajustes
                de inventario
              </span>
            </label>

            <label className="inventory-switch">
              <input
                type="checkbox"
                name="require_note_for_exits"
                checked={form.require_note_for_exits}
                onChange={handleChange}
              />
              <span>
                Exigir una nota para registrar salidas manuales
              </span>
            </label>
          </div>
        </section>

        <section className="inventory-settings-section inventory-danger-zone">
          <h4>Estado del módulo</h4>

          <label className="inventory-switch">
            <input
              type="checkbox"
              name="is_active"
              checked={form.is_active}
              onChange={handleChange}
            />
            <span>
              Habilitar el módulo de inventario para esta empresa
            </span>
          </label>
        </section>
      </fieldset>

      <div className="inventory-settings-actions">
        {!readOnly ? (
          <button type="submit" disabled={saving}>
            {saving
              ? "Guardando..."
              : "Guardar configuración"}
          </button>
        ) : (
          <span className="settings-view-only">
            Solo consulta
          </span>
        )}
      </div>
    </form>
  );
}